import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { AutocompleteInput } from "@/components/ui/autocomplete-input";
import { X, Filter, Target, Users, ChevronDown, Mic2, Building, MapPin } from "lucide-react";


interface FilterOption {
  id: number;
  value: string;
  label: string;
  description?: string;
  category: string;
  isActive: boolean;
}


interface AdvancedFilterProps {
  onFiltersChange: (filters: AdvancedFilters) => void;
  currentFilters: AdvancedFilters;
  browsePage?: 'artists' | 'venues' | 'markets';
}

export interface AdvancedFilters {
  // Name fields (separate for each type)
  artistName: string;
  venueName: string;
  
  // Basic filters (handled outside of advanced filters)
  location: string;
  dateFrom: string;
  dateTo: string;
  
  // Artist filters (now support multiple selections)
  genre: string[];
  careerStage: string[];
  setArtist: string;  // "yes", "no", or "" (any)

  // Venue filters (now support multiple selections)
  venueType: string[];
  venueLocation: string;
  
  // Keep audience filters in interface for compatibility but remove from UI
  targetAudience: {
    fanGender: string[];
    fanEthnicity: string[];
    fanAge: string[];
    fanIncome: string[];
    fanAttributes: string[];
    fanGeo?: string[];
  };
  
  // Keep minMatchPercentage for compatibility but remove from UI
  minMatchPercentage: number;
}

// Helper function to get filter options by category
const useFilterOptions = (category: string) => {
  const { data = [] } = useQuery<FilterOption[]>({
    queryKey: ['/api/filter-options', category],
    queryFn: async () => {
      const response = await fetch(`/api/filter-options?category=${category}`);
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      return response.json();
    },
  });
  
  return data
    .filter(option => option.isActive)
    .map(option => ({ value: option.value, label: option.label }));
};

interface AdvancedFilterContentProps {
  onFiltersChange: (filters: AdvancedFilters) => void;
  onFiltersChangeKeepOpen?: (filters: AdvancedFilters) => void;
  currentFilters: AdvancedFilters;
  browsePage?: 'artists' | 'venues' | 'markets';
}

export function AdvancedFilterContent({ onFiltersChange, onFiltersChangeKeepOpen, currentFilters, browsePage = 'artists' }: AdvancedFilterContentProps) {

  const [filters, setFilters] = useState<AdvancedFilters>(currentFilters);
  
  // Use a ref to store the callback to prevent closure issues
  const callbackRef = useRef(onFiltersChange);
  
  useEffect(() => {
    callbackRef.current = onFiltersChange;
  }, [onFiltersChange]);

  
  // Fetch filter options from API
  const genreOptions = useFilterOptions('genre');
  const careerStageOptions = useFilterOptions('careerStage');
  const venueTypeOptions = useFilterOptions('venueType');

  // Dynamic naming based on browse page
  const getNameLabel = () => {
    switch (browsePage) {
      case 'venues': return 'Venue Name';
      case 'markets': return 'Market Name';
      default: return 'Artist Name';
    }
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    // Clamp date fields to today if a past date is typed manually
    if ((key === 'dateFrom' || key === 'dateTo') && typeof value === 'string' && value) {
      const today = new Date().toISOString().split('T')[0];
      if (value < today) value = today;
    }
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
  };


  const applyFilters = () => {
    // Try both the ref and the prop
    const callback = callbackRef.current || onFiltersChange;
    
    if (callback) {
      callback(filters);
    }
  };

  const clearFilters = () => {
    const clearedFilters: AdvancedFilters = {
      artistName: "",
      venueName: "",
      // Keep location from parent but clear date fields within advanced filters
      location: filters.location,
      dateFrom: "",
      dateTo: "",
      genre: [],
      careerStage: [],
      setArtist: "",
      venueType: [],
      venueLocation: "",
      // Keep existing audience filters - don't clear since they're managed by PersonaBar
      targetAudience: filters.targetAudience,
      minMatchPercentage: filters.minMatchPercentage
    };
    setFilters(clearedFilters);
    
    // Only update local state, don't trigger any callbacks
    // The user needs to click "Apply Filters" to actually apply the cleared filters
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.artistName) count++;
    if (filters.venueName) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.genre && filters.genre.length > 0) count++;
    if (filters.careerStage && filters.careerStage.length > 0) count++;
    if (filters.venueType && filters.venueType.length > 0) count++;
    if (filters.venueLocation) count++;
    
    // Don't count audience filters since they're managed by PersonaBar
    
    return count;
  };

  // Multi-select dropdown component (stays open version)
  const renderMultiSelectFilter = (title: string, options: {value: string, label: string}[], values: string[], onChange: (values: string[]) => void, icon: React.ReactNode) => {
    const [searchTerm, setSearchTerm] = useState("");
    
    const toggleOption = (value: string) => {
      const newValues = values.includes(value) 
        ? values.filter(v => v !== value)
        : [...values, value];
      onChange(newValues);
    };

    const displayText = values.length === 0 
      ? `Any ${title.toLowerCase()}`
      : `${values.length} selected`;

    // Filter options based on search term
    const filteredOptions = options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          {icon}
          <Label className="text-sm font-medium">{title}</Label>
        </div>
        
        {/* Multi-select dropdown with badges inside */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full h-9 justify-between">
              <span className="truncate">{displayText}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <div className="p-2">
              {/* Search input */}
              <div className="pb-2">
                <Input
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              
              <div className="max-h-48 overflow-auto space-y-1">
                <div
                  className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 cursor-pointer rounded border-b"
                  onClick={() => onChange([])}
                >
                  <Checkbox checked={values.length === 0} />
                  <span className="text-sm">Any {title.toLowerCase()}</span>
                </div>
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 cursor-pointer rounded"
                    onClick={() => toggleOption(option.value)}
                  >
                    <Checkbox checked={values.includes(option.value)} />
                    <span className="text-sm">{option.label}</span>
                  </div>
                ))}
                {filteredOptions.length === 0 && searchTerm && (
                  <div className="px-2 py-1 text-sm text-gray-500">
                    No results found for "{searchTerm}"
                  </div>
                )}
              </div>
              
              {/* Show selected values as badges inside dropdown */}
              {values.length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <div className="flex flex-wrap gap-1 w-full overflow-hidden">
                    {values.map((value) => {
                      const option = options.find(o => o.value === value);
                      return (
                        <Badge key={value} variant="secondary" className="text-xs max-w-[100px] flex items-center shrink-0">
                          <span className="truncate">{option?.label}</span>
                          <X 
                            className="w-3 h-3 ml-1 cursor-pointer flex-shrink-0" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  // Single-select dropdown component for Yes/No style filters
  const renderSingleSelectFilter = (title: string, options: {value: string, label: string}[], value: string, onChange: (value: string) => void, icon: React.ReactNode) => {
    const displayText = !value
      ? `Any`
      : options.find(o => o.value === value)?.label || value;

    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          {icon}
          <Label className="text-sm font-medium">{title}</Label>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full h-9 justify-between">
              <span className="truncate">{displayText}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <div className="p-2">
              <div className="max-h-48 overflow-auto space-y-1">
                <div
                  className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 cursor-pointer rounded border-b"
                  onClick={() => onChange('')}
                >
                  <Checkbox checked={!value} />
                  <span className="text-sm">Any</span>
                </div>
                {options.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 cursor-pointer rounded"
                    onClick={() => onChange(option.value)}
                  >
                    <Checkbox checked={value === option.value} />
                    <span className="text-sm">{option.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  // Static SET Artist options
  const setArtistOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];


  return (
    <div className="space-y-6">

      {/* Date Range Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Date Range</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date From</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              />
            </div>
            <div>
              <Label>Date To</Label>
              <Input
                type="date"
                value={filters.dateTo}
                min={filters.dateFrom || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Artist and Venue Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Artist Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Artist Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Artist Name</Label>
              <AutocompleteInput
                value={filters.artistName || ""}
                onChange={(value) => handleFilterChange("artistName", value)}
                onSelect={(value) => handleFilterChange("artistName", value)}
                placeholder="Search artist names..."
                endpoint="/api/autocomplete/artists"
                icon={<Mic2 className="h-4 w-4 text-gray-400" />}
              />
            </div>
            
            {renderMultiSelectFilter("Genre", genreOptions, filters.genre, (values) => handleFilterChange("genre", values), <Users className="w-4 h-4" />)}
            
            {renderMultiSelectFilter("Career Stage", careerStageOptions, filters.careerStage, (values) => handleFilterChange("careerStage", values), <Target className="w-4 h-4" />)}

            {renderSingleSelectFilter("SET Artist", setArtistOptions, filters.setArtist || '', (value) => handleFilterChange("setArtist", value), <Target className="w-4 h-4" />)}
          </CardContent>
        </Card>

        {/* Venue Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Venue Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Venue Name</Label>
              <AutocompleteInput
                value={filters.venueName || ""}
                onChange={(value) => handleFilterChange("venueName", value)}
                onSelect={(value) => handleFilterChange("venueName", value)}
                placeholder="Search venue names..."
                endpoint="/api/autocomplete/venues"
                icon={<Building className="h-4 w-4 text-gray-400" />}
              />
            </div>
            
            {renderMultiSelectFilter("Type", venueTypeOptions, filters.venueType, (values) => handleFilterChange("venueType", values), <Filter className="w-4 h-4" />)}
            
            <div>
              <Label>Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={filters.location || ""}
                  onChange={(e) => handleFilterChange("location", e.target.value)}
                  placeholder="City, State (e.g., Austin, TX or Texas, New York)"
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={clearFilters}>
          Clear Filters
        </Button>
        <Button onClick={applyFilters}>
          Apply Filters
        </Button>
      </div>
    </div>
  );
}

// Keep the original dialog version for backwards compatibility
export default function AdvancedFilter({ onFiltersChange, currentFilters, browsePage }: AdvancedFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleFiltersChange = (filters: AdvancedFilters) => {
    onFiltersChange(filters);
    setIsOpen(false);
  };

  const handleFiltersChangeKeepOpen = (filters: AdvancedFilters) => {
    onFiltersChange(filters);
    // Don't close the dialog - keep it open for further selections
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="relative">
          <Filter className="w-4 h-4 mr-2" />
          Advanced Filters
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Target className="w-5 h-5" />
            <span>Advanced Filtering</span>
          </DialogTitle>
          <DialogDescription>
            Filter by basic criteria, artist/venue attributes, and target audience demographics
          </DialogDescription>
        </DialogHeader>
        
        <AdvancedFilterContent 
          currentFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
          onFiltersChangeKeepOpen={handleFiltersChangeKeepOpen}
          browsePage={browsePage}
        />
      </DialogContent>
    </Dialog>
  );
}